import { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../../utils/axios';
import { getAppDocsByApp } from '../../utils/urls';

const PAGE_SIZE = 20;

// App docs from the paginated /api/app-docs/app/<id>/ endpoint. Every load
// re-fetches pages 1..n so uploads/deletes made meanwhile can't shift items
// between pages and get skipped or duplicated.
// Ordering: 'date' | 'name' | 'size', prefixed with '-' for descending.
export default function useAppDocs(appId, enabled) {
    const [appDocs, setAppDocs]   = useState([]);
    const [pageCount, setPageCount] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore]   = useState(false);
    const [loading, setLoading]   = useState(false);
    const [ordering, setOrdering] = useState('-date');
    const requestRef = useRef(0);

    const load = useCallback(async (pages) => {
        if (!appId) return;
        const request = ++requestRef.current;
        setLoading(true);
        try {
            const settled = await Promise.allSettled(
                Array.from({ length: pages }, (_, i) =>
                    axios.get(`${getAppDocsByApp}${appId}/?ordering=${ordering}&page=${i + 1}&page_size=${PAGE_SIZE}`))
            );
            // A newer load (e.g. a sort change) has started; drop this result.
            if (request !== requestRef.current) return;
            // Trailing pages can 404 if docs were deleted since they were loaded.
            const loaded = settled.filter(r => r.status === 'fulfilled' && r.value.status === 200).map(r => r.value.data);
            // A failed page also stops paging, so infinite scroll can't retry in a loop.
            if (!loaded.length) { setHasMore(false); return; }
            const seen = new Set();
            setAppDocs(loaded.flatMap(d => d.results || []).filter(d => !seen.has(d.app_doc_id) && seen.add(d.app_doc_id)));
            setPageCount(loaded.length);
            setTotalCount(loaded[loaded.length - 1].count || 0);
            setHasMore(loaded.length === pages && !!loaded[loaded.length - 1].next);
        } finally {
            if (request === requestRef.current) setLoading(false);
        }
    }, [appId, ordering]);

    // Also re-runs from page 1 whenever the ordering changes.
    useEffect(() => {
        if (enabled) load(1);
    }, [enabled, load]);

    const reloadAppDocs   = useCallback(() => load(pageCount), [load, pageCount]);
    const loadMoreAppDocs = useCallback(() => load(pageCount + 1), [load, pageCount]);

    return {
        appDocs, setAppDocs, reloadAppDocs, loadMoreAppDocs,
        appDocsHasMore: hasMore, appDocsLoading: loading, appDocsTotal: totalCount,
        appDocsOrdering: ordering, setAppDocsOrdering: setOrdering,
    };
}
